import React, { useState, useEffect } from 'react';
import { Clock, User, FileText, CheckCircle2, X, Tag } from 'lucide-react';
import { approvalAPI, getImageUrl } from '../services/api';
import { useToast } from '../contexts/ToastContext';
import ParticlesBackground from '../components/ParticlesBackground';
import ContentDiffViewer from '../components/ContentDiffViewer';
import RejectConfirmationModal from '../components/RejectConfirmationModal';
import {
  ApprovalContainer,
  Header,
  Title,
  Subtitle,
  FilterSection,
  FilterLabel,
  FilterSelect,
  PendingList,
  PendingCard,
  CardHeader,
  CardTitle,
  ContentTypeBadge,
  CardMeta,
  MetaItem,
  EmptyState,
  EmptyStateIcon,
  EmptyStateText,
  LoadingContainer,
  ErrorContainer,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalTitle,
  CloseButton,
  ModalBody,
  ModalFooter,
  ApproveButton,
  RejectButton,
  CancelButton,
  InfoSection,
  InfoRow,
  InfoLabel,
  InfoValue,
  DiffContainer,
  DiffTitle,
  UpdateBadge,
  NewContentBadge,
} from './SuperAdminApproval.styles';

const CONTENT_TYPES = [
  { value: '', label: 'All Content Types' },
  { value: 'hero_section', label: 'Hero Section', color: '#C5337B' },
  { value: 'about_us', label: 'About Us', color: '#2B3857' },
  { value: 'services', label: 'Services', color: '#E91E63' },
  { value: 'how_it_works', label: 'How It Works', color: '#8B5CF6' },
  { value: 'our_team', label: 'Our Team', color: '#06B6D4' },
  { value: 'blog', label: 'Blog', color: '#8B5CF6' },
  { value: 'testimonials', label: 'Testimonials', color: '#F59E0B' },
  { value: 'pricing', label: 'Pricing', color: '#10B981' },
  { value: 'service_page', label: 'Service Page', color: '#3B82F6' },
];

const SuperAdminApproval = () => {
  const { showSuccess, showError } = useToast();
  const [pendingItems, setPendingItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedFilter, setSelectedFilter] = useState('');
  const [selectedItem, setSelectedItem] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);

  // Fetch pending content
  const fetchPendingContent = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await approvalAPI.getPendingContent(selectedFilter || null);
      setPendingItems(response.data.pending_items || []);
    } catch (err) {
      console.error('Error fetching pending content:', err);
      setError(err.response?.data?.error || 'Failed to load pending content');
      showError('Failed to load pending content');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPendingContent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFilter]);

  // Handle viewing item details
  const handleViewItem = async (item) => {
    try {
      setDetailsLoading(true);
      const response = await approvalAPI.getContentDetails(item.content_type, item.id);
      setSelectedItem(response.data);
    } catch (err) {
      console.error('Error fetching content details:', err);
      showError('Failed to load content details');
    } finally {
      setDetailsLoading(false);
    }
  };

  // Handle approval
  const handleApprove = async () => {
    if (!selectedItem) return;

    try {
      setActionLoading(true);
      await approvalAPI.approveContent(selectedItem.content_type, selectedItem.pending_content.id);
      showSuccess('Content approved successfully!');
      setSelectedItem(null);
      fetchPendingContent(); // Refresh the list
    } catch (err) {
      console.error('Error approving content:', err);
      showError(err.response?.data?.error || 'Failed to approve content');
    } finally {
      setActionLoading(false);
    }
  };

  // Handle rejection
  const handleReject = async () => {
    if (!selectedItem) return;

    try {
      setActionLoading(true);
      await approvalAPI.rejectContent(selectedItem.content_type, selectedItem.pending_content.id);
      showSuccess('Content rejected');
      setShowRejectModal(false);
      setSelectedItem(null);
      fetchPendingContent(); // Refresh the list
    } catch (err) {
      console.error('Error rejecting content:', err);
      showError(err.response?.data?.error || 'Failed to reject content');
    } finally {
      setActionLoading(false);
    }
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Get content type color
  const getContentTypeColor = (type) => {
    const contentType = CONTENT_TYPES.find(ct => ct.value === type);
    return contentType?.color || '#C5337B';
  };

  // Get content title for rejection confirmation
  const getContentTitle = () => {
    if (!selectedItem) return '';
    const content = selectedItem.pending_content;
    return content.title || content.heading || content.page_title || content.page_heading || 'Content';
  };

  // Render field diff
  const renderFieldDiffs = () => {
    if (!selectedItem) return null;

    const pending = selectedItem.pending_content;
    const approved = selectedItem.approved_content;
    const isNew = !selectedItem.is_update;

    // Fields to exclude from diff view
    const excludeFields = [
      'id', 'approval_status', 'submitted_by', 'submitted_at', 
      'approved_by', 'approved_at', 'created_at', 'updated_at'
    ];

    // Get all fields from pending content
    const fields = Object.keys(pending).filter(key => !excludeFields.includes(key));

    return fields.map(field => (
      <ContentDiffViewer
        key={field}
        fieldName={field}
        oldValue={approved ? approved[field] : null}
        newValue={pending[field]}
        isNew={isNew}
      />
    ));
  };

  return (
    <>
      <ApprovalContainer>
        <ParticlesBackground />

        <Header>
          <Title>Content Approval</Title>
          <Subtitle>Review and approve pending content submissions</Subtitle>
        </Header>

        <FilterSection>
          <FilterLabel>Filter by type:</FilterLabel>
          <FilterSelect
            value={selectedFilter}
            onChange={(e) => setSelectedFilter(e.target.value)}
          >
            {CONTENT_TYPES.map(type => (
              <option key={type.value} value={type.value}>
                {type.label}
              </option>
            ))}
          </FilterSelect>
        </FilterSection>

        {error && (
          <ErrorContainer>
            <strong>Error:</strong> {error}
          </ErrorContainer>
        )}

        {loading ? (
          <LoadingContainer>
            <div>Loading pending content...</div>
          </LoadingContainer>
        ) : pendingItems.length === 0 ? (
          <EmptyState>
            <EmptyStateIcon>📭</EmptyStateIcon>
            <EmptyStateText>
              {selectedFilter 
                ? `No pending ${CONTENT_TYPES.find(t => t.value === selectedFilter)?.label} content` 
                : 'No pending content to review'}
            </EmptyStateText>
          </EmptyState>
        ) : (
          <PendingList>
            {pendingItems.map(item => (
              <PendingCard key={item.id} onClick={() => handleViewItem(item)}>
                <CardHeader>
                  <div>
                    <CardTitle>{item.preview}</CardTitle>
                  </div>
                  <ContentTypeBadge color={`${getContentTypeColor(item.content_type)}33`} textColor={getContentTypeColor(item.content_type)}>
                    {item.content_type.replace(/_/g, ' ')}
                  </ContentTypeBadge>
                </CardHeader>
                <CardMeta>
                  <MetaItem>
                    <Tag />
                    {CONTENT_TYPES.find(t => t.value === item.content_type)?.label || item.content_type.replace(/_/g, ' ')}
                  </MetaItem>
                  <MetaItem>
                    <Clock />
                    {formatDate(item.submitted_at)}
                  </MetaItem>
                  <MetaItem>
                    <User />
                    Submitted by Admin
                  </MetaItem>
                  <MetaItem>
                    <FileText />
                    Pending Review
                  </MetaItem>
                </CardMeta>
              </PendingCard>
            ))}
          </PendingList>
        )}
      </ApprovalContainer>

      {/* Detail Modal */}
      {selectedItem && (
        <ModalOverlay onClick={() => !actionLoading && setSelectedItem(null)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>
                {selectedItem.is_update ? 'Review Content Update' : 'Review New Content'}
              </ModalTitle>
              <CloseButton onClick={() => !actionLoading && setSelectedItem(null)}>
                <X size={24} />
              </CloseButton>
            </ModalHeader>

            <ModalBody>
              {detailsLoading ? (
                <LoadingContainer>
                  <div>Loading details...</div>
                </LoadingContainer>
              ) : (
                <>
                  <InfoSection>
                    <InfoRow>
                      <InfoLabel>Content Type</InfoLabel>
                      <InfoValue>{selectedItem.content_type.replace(/_/g, ' ').toUpperCase()}</InfoValue>
                    </InfoRow>
                    <InfoRow>
                      <InfoLabel>Submitted By</InfoLabel>
                      <InfoValue>{selectedItem.submitter.name} ({selectedItem.submitter.email})</InfoValue>
                    </InfoRow>
                    <InfoRow>
                      <InfoLabel>Submitted At</InfoLabel>
                      <InfoValue>{formatDate(selectedItem.submitted_at)}</InfoValue>
                    </InfoRow>
                    <InfoRow>
                      <InfoLabel>Action Type</InfoLabel>
                      <InfoValue>
                        {selectedItem.is_update ? (
                          <UpdateBadge>Update</UpdateBadge>
                        ) : (
                          <NewContentBadge>New</NewContentBadge>
                        )}
                      </InfoValue>
                    </InfoRow>
                  </InfoSection>

                  <DiffContainer>
                    <DiffTitle>
                      <FileText size={20} />
                      Content Comparison
                    </DiffTitle>
                    {renderFieldDiffs()}
                  </DiffContainer>
                </>
              )}
            </ModalBody>

            <ModalFooter>
              <CancelButton onClick={() => setSelectedItem(null)} disabled={actionLoading}>
                Close
              </CancelButton>
              <RejectButton 
                onClick={() => setShowRejectModal(true)} 
                disabled={actionLoading || detailsLoading}
              >
                <X size={18} />
                Reject
              </RejectButton>
              <ApproveButton 
                onClick={handleApprove} 
                disabled={actionLoading || detailsLoading}
              >
                <CheckCircle2 size={18} />
                {actionLoading ? 'Approving...' : 'Approve'}
              </ApproveButton>
            </ModalFooter>
          </ModalContent>
        </ModalOverlay>
      )}

      {/* Reject Confirmation Modal */}
      <RejectConfirmationModal
        isOpen={showRejectModal}
        onClose={() => setShowRejectModal(false)}
        onConfirm={handleReject}
        contentTitle={getContentTitle()}
        loading={actionLoading}
      />
    </>
  );
};

export default SuperAdminApproval;

